import { useCallback, useMemo, useState } from "react";
import {
  DMAutomationContent,
  DMAutomationContentType,
  NotificationType,
} from "@markit/common.types";
import { detectedCensored, MARKIT_DOCS } from "@markit/common.utils";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { BackButton } from "../../../Buttons/BackButton";
import RectangleButton from "../../../Buttons/RectangleButton";
import PreviewBoxContainer from "../../../Containers/PreviewBoxContainer";
import CustomTextFieldMultiline from "../../../TextFields/CustomTextFieldMultiline";
import { useTheme } from "../../../../hooks/useTheme";
import { API } from "../../../../API";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import ContentViolationAlert from "../../../DeliveryScore/ContentViolationAlert";
import ConfirmDiscardChangesModal from "../../../Containers/ConfirmPopups/ConfirmDiscardChangesModal";
import TextHoverButton from "../../../Buttons/TextHoverButton";
import {
  FOLLOW_GATE_PROMPT_TEXT,
  PHONE_PROMPT_STATIC_PREFIX,
} from "../../../../utils/instagramPresetUtils";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import AlertContainer from "../../../Containers/AlertContainer";
import SwitchToggleItem from "../../../SwitchToggleItem";
import RemoveComplianceConfirmModal from "../../../Compliance/RemoveComplianceConfirmModal";
import PhoneComplianceTerms from "../../../Compliance/PhoneComplianceTerms";

type CustomizablePromptType =
  | DMAutomationContentType.PHONE_PROMPT
  | DMAutomationContentType.REQUIRE_FOLLOW_PROMPT;

const PROMPT_CONFIG: Record<
  CustomizablePromptType,
  { title: string; description: string; presetText: string }
> = {
  [DMAutomationContentType.PHONE_PROMPT]: {
    title: "Phone Number Prompt",
    description:
      "The initial DM sent when someone comments to trigger this automation. Asks for their phone number so your content can be texted to them.",
    presetText: PHONE_PROMPT_STATIC_PREFIX,
  },
  [DMAutomationContentType.REQUIRE_FOLLOW_PROMPT]: {
    title: "Require Follow Prompt",
    description:
      "This DM is sent if the user isn't following yet. Includes a button they press after following to confirm and continue.",
    presetText: FOLLOW_GATE_PROMPT_TEXT,
  },
};

type DMAutomationPromptsPanelProps = {
  dmAutomationId: string;
  sendAsText: boolean;
  requireFollow: boolean;
  removeCompliance: boolean;
  setRemoveCompliance: (removeCompliance: boolean) => void;
  isEditing: boolean;
  promptContents: DMAutomationContent[];
  setPromptContents: (contents: DMAutomationContent[]) => void;
  backOnPress: () => void;
};

const DMAutomationPromptsPanel = (props: DMAutomationPromptsPanelProps) => {
  const {
    dmAutomationId,
    sendAsText,
    requireFollow,
    removeCompliance,
    setRemoveCompliance,
    isEditing,
    promptContents,
    setPromptContents,
    backOnPress,
  } = props;
  const accountData = useSelector(getAccountSlice).accountData;
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [violation, setViolation] = useState<{
    type: CustomizablePromptType;
    message: string;
  } | null>(null);
  const [showAlert, setShowAlert] = useState("");
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showRemoveComplianceModal, setShowRemoveComplianceModal] =
    useState(false);
  // Held locally until Save is pressed, same as the prompt text fields.
  const [localRemoveCompliance, setLocalRemoveCompliance] =
    useState(removeCompliance);

  const onRemoveComplianceToggle = useCallback(() => {
    if (localRemoveCompliance) {
      setLocalRemoveCompliance(false);
    } else {
      setShowRemoveComplianceModal(true);
    }
  }, [localRemoveCompliance]);

  const infoIcon = useCallback(
    (url: string) => (
      <Icon
        icon="mdi:information-outline"
        height={14}
        style={{ color: Colors.GRAY1, cursor: "pointer" }}
        onClick={() => window.open(url, "_blank")}
      />
    ),
    []
  );

  const styles = {
    headerRow: { flexShrink: 0, height: 58, paddingInline: 14 },
    urlButton: {
      backgroundColor: Colors.WHITE,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 4,
      padding: "4px 10px",
      width: 100,
      textAlign: "center" as const,
    },
  };

  const visibleTypes: CustomizablePromptType[] = useMemo(() => {
    const types: CustomizablePromptType[] = [];
    if (sendAsText) {
      types.push(DMAutomationContentType.PHONE_PROMPT);
    }
    if (requireFollow) {
      types.push(DMAutomationContentType.REQUIRE_FOLLOW_PROMPT);
    }
    return types;
  }, [sendAsText, requireFollow]);

  const existingContentForType = useCallback(
    (type: CustomizablePromptType) =>
      promptContents.find((content) => content.type === type),
    [promptContents]
  );

  const getBaselineMessage = useCallback(
    (type: CustomizablePromptType) =>
      existingContentForType(type)?.message ?? PROMPT_CONFIG[type].presetText,
    [existingContentForType]
  );

  // Seeded once from whatever's already customized, else the preset.
  const [localMessages, setLocalMessages] = useState<
    Record<CustomizablePromptType, string>
  >(() => {
    const initial = {} as Record<CustomizablePromptType, string>;
    visibleTypes.forEach((type) => {
      initial[type] = getBaselineMessage(type);
    });
    return initial;
  });

  const madeChanges = useMemo(
    () =>
      visibleTypes.some(
        (type) =>
          (localMessages[type] ?? "").trim() !== getBaselineMessage(type).trim()
      ) || localRemoveCompliance !== removeCompliance,
    [
      visibleTypes,
      localMessages,
      getBaselineMessage,
      localRemoveCompliance,
      removeCompliance,
    ]
  );

  const closeOnPress = () => {
    if (madeChanges) {
      setShowDiscardChangesModal(true);
    } else {
      backOnPress();
    }
  };

  const saveOnPress = useCallback(async () => {
    for (const type of visibleTypes) {
      const message = localMessages[type].trim() ?? "";
      if (detectedCensored(message)) {
        setViolation({ type, message });
        return;
      }
      if (!message) {
        setShowAlert(`${PROMPT_CONFIG[type].title} cannot be empty.`);
        return;
      }
    }

    setSaving(true);
    // First, handle updating the prompt contents
    let updatedPromptContents = [...promptContents];
    for (const type of visibleTypes) {
      const existingContent = existingContentForType(type);
      const message = localMessages[type].trim();

      if (message === PROMPT_CONFIG[type].presetText) {
        // If the message is changed back to the preset, delete the existing content.
        if (existingContent) {
          if (isEditing && existingContent.id) {
            await API.dmAutomation.deleteDmAutomationContent({
              dmAutomationId,
              contentId: existingContent.id,
            });
          }
          updatedPromptContents = updatedPromptContents.filter(
            (c) => c.type !== type
          );
        }
        continue;
      }

      const updatedContent: DMAutomationContent = {
        ...existingContent,
        id: existingContent?.id ?? "",
        dmAutomationId,
        createdAt: existingContent?.createdAt ?? new Date().toISOString(),
        createdBy: accountData.uid,
        message,
        buttons: existingContent?.buttons ?? [],
        type,
      };
      if (isEditing) {
        // Automation already published - persist immediately
        const { contentId } = await API.dmAutomation.writeDmAutomationContent({
          content: updatedContent,
        });
        updatedPromptContents = [
          ...updatedPromptContents.filter((c) => c.type !== type),
          { ...updatedContent, id: contentId },
        ];
      } else {
        // Still in the creation flow - hold in local state until the automation is published
        updatedPromptContents = [
          ...updatedPromptContents.filter((c) => c.type !== type),
          updatedContent,
        ];
      }
    }
    setPromptContents(updatedPromptContents);

    // Second, handle updating the remove compliance setting
    if (localRemoveCompliance !== removeCompliance) {
      setRemoveCompliance(localRemoveCompliance);
      if (isEditing) {
        await API.dmAutomation.updateDmAutomation({
          dmAutomationId,
          requireFollow,
          removeCompliance: localRemoveCompliance,
        } as any);
      }
    }

    setSaving(false);
    if (isEditing) {
      showNotificationBanner(
        dispatch,
        "Prompts Updated!",
        NotificationType.AFFIRMATIVE
      );
    }
    backOnPress();
  }, [
    visibleTypes,
    localMessages,
    promptContents,
    existingContentForType,
    isEditing,
    dmAutomationId,
    accountData.uid,
    setPromptContents,
    localRemoveCompliance,
    removeCompliance,
    setRemoveCompliance,
    requireFollow,
    dispatch,
    backOnPress,
  ]);

  const resetOnPress = useCallback(
    (type: CustomizablePromptType) => {
      setLocalMessages((prev) => ({
        ...prev,
        [type]: PROMPT_CONFIG[type].presetText,
      }));
    },
    [setLocalMessages]
  );

  return (
    <div
      className="ColumnNormal PreventScroll"
      style={{ backgroundColor: Colors.WHITE }}
    >
      <div className="AlignedRowSpaced" style={styles.headerRow}>
        <div className="AlignedRow" style={{ gap: 10 }}>
          <BackButton onPress={closeOnPress} iconName="ion:chevron-back" />
          <span className="sectionTitle">Customize DM Prompts</span>
        </div>
        <RectangleButton
          buttonLabel="Save"
          onPress={saveOnPress}
          loading={saving}
          disabled={!madeChanges}
          altColor={Colors.BLACK}
          altTextColor={Colors.WHITE}
          altBorderRadius={100}
          containerStyles={{ padding: "10px 14px", width: "fit-content" }}
        />
      </div>
      <HorizontalDivider altMargin={0} />
      <div
        className="ColumnNormal AllowScroll"
        style={{ padding: 14, gap: 14 }}
      >
        {visibleTypes.map((type) => {
          const config = PROMPT_CONFIG[type];
          const isPhonePrompt = type === DMAutomationContentType.PHONE_PROMPT;
          const isRequireFollowPrompt =
            type === DMAutomationContentType.REQUIRE_FOLLOW_PROMPT;
          return (
            <PreviewBoxContainer
              key={type}
              message=""
              containerStyles={{
                borderRadius: 12,
                backgroundColor: Colors.WHITE1,
                border: `1.5px solid ${theme.DividerColor.borderColor}`,
              }}
              headerComp={<span className="sectionTitle">{config.title}</span>}
              subHeaderComp={
                <span style={{ fontSize: 12, color: Colors.GRAY1 }}>
                  {config.description}
                </span>
              }
              headerRightComp={null}
              noDivider
              alternateBodyComp={
                <div
                  className="ColumnNormal"
                  style={{ marginTop: 14, gap: 14 }}
                >
                  <div
                    className="ColumnNormal"
                    style={{ paddingInline: 14, gap: 7 }}
                  >
                    <div
                      className="ColumnNormal"
                      style={{
                        gap: 10,
                        border: `1px solid ${theme.EditableTextFieldBorder.borderColor}`,
                        borderRadius: 12,
                        backgroundColor: Colors.WHITE1,
                      }}
                    >
                      <CustomTextFieldMultiline
                        placeholder={config.presetText}
                        value={localMessages[type] ?? ""}
                        onChange={(e) =>
                          setLocalMessages((prev) => ({
                            ...prev,
                            [type]: e.target.value,
                          }))
                        }
                        maxLength={500}
                        containerStyles={{
                          height: 70,
                          border: "none",
                          backgroundColor: "transparent",
                        }}
                      />
                      {isPhonePrompt && !localRemoveCompliance ? (
                        <div
                          className="ColumnNormal"
                          style={{ padding: "0 14px 14px 14px" }}
                        >
                          <PhoneComplianceTerms
                            textStyles={{ color: Colors.GRAY1 }}
                          />
                        </div>
                      ) : null}
                      {isRequireFollowPrompt ? (
                        <div
                          className="ColumnNormal"
                          style={{ padding: "0 14px 14px 14px", gap: 7 }}
                        >
                          <div
                            className="smallBodySubtext"
                            style={styles.urlButton}
                          >
                            I&apos;m Following
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div
                      className="AlignedRow"
                      style={{ gap: 10, alignSelf: "flex-end" }}
                    >
                      {localMessages[type] !==
                      PROMPT_CONFIG[type].presetText ? (
                        <TextHoverButton
                          text="Reset to Default"
                          onPress={() => resetOnPress(type)}
                          textStyles={{ color: Colors.BLACK, fontWeight: 500 }}
                        />
                      ) : null}
                      <span className="smallBodySubtext">
                        {(localMessages[type] ?? "").length}/500
                      </span>
                    </div>
                  </div>
                  {isPhonePrompt ? (
                    <>
                      <HorizontalDivider altMargin={0} />
                      <div
                        className="ColumnNormal"
                        style={{ paddingInline: 14 }}
                      >
                        <SwitchToggleItem
                          title={
                            <div className="AlignedRow" style={{ gap: 4 }}>
                              <span className="bodyMedium">
                                Remove Opt-In Compliance Language
                              </span>
                              {infoIcon(
                                MARKIT_DOCS +
                                  "/sendastextautomation#removing-the-compliance-disclosure"
                              )}
                            </div>
                          }
                          description={
                            "Choose to hide the default compliance language appended to the end of the phone number prompt DM."
                          }
                          toggleValue={localRemoveCompliance}
                          onChange={onRemoveComplianceToggle}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              }
            />
          );
        })}
      </div>
      <ContentViolationAlert
        message={violation?.message ?? ""}
        isVisible={!!violation}
        setIsVisible={(visible) => {
          if (!visible) setViolation(null);
        }}
      />
      {showAlert ? (
        <AlertContainer
          headerComp={showAlert}
          closeModal={() => setShowAlert("")}
        />
      ) : null}
      <ConfirmDiscardChangesModal
        isVisible={showDiscardChangesModal}
        setIsVisible={setShowDiscardChangesModal}
        deleteOnPress={backOnPress}
      />
      {showRemoveComplianceModal ? (
        <RemoveComplianceConfirmModal
          isVisible={showRemoveComplianceModal}
          setIsVisible={setShowRemoveComplianceModal}
          confirmOnPress={() => setLocalRemoveCompliance(true)}
        />
      ) : null}
    </div>
  );
};

export default DMAutomationPromptsPanel;
